/**
 * How strong a password looks, from what a browser can actually tell.
 *
 * S-D2 screen 02 says "Weak — this appears in known breach lists", and that is
 * the one claim this cannot make. A breach check means Have I Been Pwned's
 * k-anonymity API: a third-party request from every keystroke on the signup
 * form, a vendor with no line in COGS, and a dependency on somebody else's
 * uptime at the moment a customer is trying to give AugRank money. So the meter
 * reports what it can measure — length, and whether the characters repeat — and
 * the copy says that rather than implying a check nobody ran.
 *
 * The thresholds follow NIST SP 800-63B, which is also what
 * `app/security/passwords.py` follows: **length is the only property that
 * reliably correlates with strength**, so there are no composition rules and no
 * "add a symbol" nagging. Twelve characters is the API's floor; sixteen or a
 * three-word passphrase is where the meter stops asking for more.
 *
 * The level is never the whole message. `.app-strength` colours three bars and
 * the label states the same thing in words, because colour alone is not a state
 * (CLAUDE.md, frontend rules).
 */

/** Mirrors `MIN_PASSWORD_LENGTH` in `app/security/passwords.py`. */
export const MIN_PASSWORD = 12

/** Where a longer password stops earning a better score. */
const STRONG_LENGTH = 16

/** Below this many distinct characters, length is padding rather than entropy. */
const MIN_DISTINCT = 5

export interface PasswordStrength {
  /** 0 when nothing has been typed; 1 weak, 2 acceptable, 3 strong. */
  level: 0 | 1 | 2 | 3
  /** One word, for people who cannot see the bars. */
  label: string
  /** What would make it better, or why it is fine. */
  hint: string
}

/** Separate words in a passphrase — spaces and the usual joiners people use. */
function words(value: string): number {
  return value
    .trim()
    .split(/[\s._·-]+/)
    .filter((part) => part.length >= 3).length
}

export function passwordStrength(value: string): PasswordStrength {
  if (!value) {
    return {
      level: 0,
      label: '',
      hint: `At least ${String(MIN_PASSWORD)} characters. A passphrase is fine.`,
    }
  }

  if (new Set(value).size < MIN_DISTINCT) {
    // `aaaaaaaaaaaaaaa` clears the length floor and is worthless. Caught before
    // the length branch, so a long repetitive string is never called strong.
    return {
      level: 1,
      label: 'Too repetitive',
      hint: 'Repeated characters do not add strength. Try three unrelated words.',
    }
  }

  if (value.length < MIN_PASSWORD) {
    const missing = MIN_PASSWORD - value.length
    return {
      level: 1,
      label: 'Too short',
      hint: `${String(missing)} more character${missing === 1 ? '' : 's'} to go.`,
    }
  }

  if (value.length >= STRONG_LENGTH || words(value) >= 3) {
    return { level: 3, label: 'Strong', hint: 'Long enough that guessing it is hopeless.' }
  }

  return {
    level: 2,
    label: 'Acceptable',
    hint: 'Longer is better — a few unrelated words beats punctuation.',
  }
}
